import { buildQueryString, getContentDispositionFilename, navigateTo, QueryStringParam } from "./utils/http";
import {
  AUTH_REQUIRED_ERROR_CODE,
  ensureSecureZoneRedirectFromError,
  ensureSecureZoneRedirectFromResponse
} from "./utils/secure-zone";

// TODO: FIXME
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class XhrStatus<T = any> {
  constructor(data: T, ok: boolean, statusCode: number, errorThrown?: string) {
    this.data = data;
    this.ok = ok;
    this.errorThrown = errorThrown;
    this.statusCode = statusCode;
  }

  readonly data: T;
  readonly ok: boolean;
  readonly errorThrown: string | undefined;
  readonly statusCode: number;
}

class XhrStatusOk<T> extends XhrStatus<T> {
  constructor(data: T) {
    super(data, true, 200);
  }
}

class XhrStatusError extends XhrStatus {
  constructor(data: unknown, errorThrown: string, statusCode: number) {
    super(data, false, statusCode, errorThrown);
  }
}

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "HEAD" | "PATCH";
type SendHttpMethod = "POST" | "PUT";

class HttpClient {
  private antiForgeryToken = "";

  setAntiForgeryToken(token: string) {
    this.antiForgeryToken = token;
  }

  getAntiForgeryToken(): string {
    return this.antiForgeryToken;
  }

  setDefaultAntiForgeryToken() {
    const antiForgeryToken = document.getElementById("global-anti-forgery-token")?.innerText;
    if (!antiForgeryToken) {
      throw Error("Missing anti forgery token");
    }
    this.setAntiForgeryToken(antiForgeryToken);
  }

  /**
   * Get resource(s)
   * @param url
   * @param values
   * @param headers
   * @param signal Request abort signal
   * @returns Unwrapped requested resource
   * @throws Server error, if response status is not OK
   */
  async getUnwrapped<T>(
    url: string,
    values?: Record<string, QueryStringParam>,
    headers?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await this.get<T>(url, values, headers, signal);
    if (!response.ok) throw new Error(response.errorThrown);

    return response.data;
  }

  /**
   * Get text/html resource
   * @param url
   * @param values
   * @param signal Request abort signal
   * @returns Unwrapped requested resource
   * @throws Server error, if response status is not OK
   */
  async getContent(
    url: string,
    values?: Record<string, string | number | boolean>,
    signal?: AbortSignal
  ): Promise<string> {
    const response = await this.get<string>(url, values, undefined, signal);
    if (!response.ok) throw new Error(response.errorThrown);

    return response.data;
  }

  /**
   * Get resource(s)
   * @param url
   * @param values
   * @param headers
   * @param signal Request abort signal
   * @returns Requested resource wrapped into status object
   */
  get<T>(
    url: string,
    values?: Record<string, QueryStringParam>,
    headers?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<XhrStatus<T>> {
    return this.getRequest(url, values, headers, undefined, signal);
  }

  getCrossOrigin<T>(url: string): Promise<XhrStatus<T>> {
    return this.getRequest(url, undefined, undefined, true);
  }

  private getRequest<T>(
    url: string,
    values?: Record<string, QueryStringParam>,
    headers?: Record<string, string>,
    cors?: boolean,
    signal?: AbortSignal
  ): Promise<XhrStatus<T>> {
    url = values ? `${url}${buildQueryString(values)}` : url;
    return this.request(url, "GET", undefined, headers, {}, !!cors, signal);
  }

  /**
   * Get resource(s) metadata
   * @param url
   * @param values
   * @param headers
   * @returns Requested resource wrapped into status object
   */
  head(url: string, values?: Record<string, unknown>, headers?: Record<string, string>): Promise<XhrStatus> {
    return this.request(url, "HEAD", values, headers);
  }

  /**
   * Post resource
   * @param url
   * @param values
   * @param headers
   * @param cors
   */
  post<T>(
    url: string,
    values: Record<string, unknown>,
    headers?: Record<string, string>,
    cors?: boolean
  ): Promise<XhrStatus<T>> {
    return this.request(url, "POST", values, headers, {}, cors);
  }

  postForm(url: string, values: Record<string, QueryStringParam>) {
    const form = document.createElement("form");
    form.method = "post";
    form.action = url;
    form.style.display = "none";

    if (this.isSameOriginRequest(url)) {
      if (!this.antiForgeryToken) {
        this.setDefaultAntiForgeryToken();
      }

      const tokenField = document.createElement("input");
      tokenField.type = "hidden";
      tokenField.name = "__RequestVerificationToken";
      tokenField.value = this.antiForgeryToken;
      form.appendChild(tokenField);
    }

    for (const [key, value] of Object.entries(values)) {
      if (value === undefined || value === null) {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          if (item === undefined || item === null) {
            continue;
          }
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = String(item);
          form.appendChild(input);
        }
        continue;
      }

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
    requestAnimationFrame(() => {
      form.remove();
    });
  }

  /**
   * Put resource
   * @param url
   * @param values
   * @param headers
   * @param cors
   */
  put<T>(
    url: string,
    values: Record<string, unknown>,
    headers?: Record<string, string>,
    cors?: boolean
  ): Promise<XhrStatus<T>> {
    return this.request(url, "PUT", values, headers, {}, cors);
  }

  /**
   * Patch resource
   * @param url
   * @param values
   * @param headers
   * @param cors
   */
  patch(url: string, values: Record<string, unknown>, headers?: Record<string, string>, cors?: boolean) {
    return this.request(url, "PATCH", values, headers, {}, cors);
  }

  /**
   * Delete resource
   * @param url
   * @param values
   * @param headers
   * @param cors
   */
  delete<T>(url: string): Promise<XhrStatus<T>>;
  delete<T>(url: string, values: Record<string, unknown>): Promise<XhrStatus<T>>;
  delete<T>(
    url: string,
    values?: Record<string, unknown>,
    headers?: Record<string, string>,
    cors?: boolean
  ): Promise<XhrStatus<T>> {
    return this.request(url, "DELETE", values, headers, {}, cors);
  }

  /**
   * Send resource
   * @param method
   * @param url
   * @param values
   * @param headers
   * @param cors
   */
  send(
    method: SendHttpMethod,
    url: string,
    values: Record<string, unknown>,
    headers?: Record<string, string>,
    files?: Record<string, Blob>,
    cors?: boolean
  ) {
    return this.request(url, method, values, headers, files, cors);
  }

  // TODO: split to different methods based on input and output, e.g. files, form, json, text etc.
  private async request<T>(
    url: string,
    method: HttpMethod,
    body?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
    files?: Record<string, Blob>,
    cors?: boolean,
    signal?: AbortSignal
  ): Promise<XhrStatus<T>> {
    let formData: FormData | undefined = undefined;

    const fileEntries = Object.values(files ?? {});
    if (fileEntries.length > 0) {
      formData = new FormData();

      for (const [key, value] of Object.entries(body ?? {})) {
        formData.set(key, value as string | Blob);
      }

      let fileIndex = 0;
      for (const value of fileEntries) {
        formData.set(`upload${fileIndex++}`, value);
      }

      body = undefined;
    }

    const headers = new Headers();

    if (this.isSameOriginRequest(url)) {
      headers.set("X-RequestVerificationToken", this.antiForgeryToken);
      this.setCurrentLanguageHeader(headers);
    }

    const contentTypeJson = "application/json";

    if (formData) {
      headers.set("Accept", contentTypeJson);
      headers.set("X-Requested-With", "XMLHttpRequest");
    } else if (!cors) {
      headers.set("Accept", contentTypeJson);
      headers.set("Content-Type", contentTypeJson);
      headers.set("X-Requested-With", "XMLHttpRequest");
    }

    if (extraHeaders) {
      for (const [key, value] of Object.entries(extraHeaders)) {
        if (headers.has(key)) {
          console.warn(`Overwriting existing header: ${key}. Value: ${value}`);
        }
        headers.set(key, value);
      }
    }

    const request: RequestInit = {
      method: method,
      body: body ? JSON.stringify(body) : formData,
      headers: headers,
      signal: signal
    };

    try {
      const response = await fetch(url, request);

      const redirectErrorCode = await ensureSecureZoneRedirectFromResponse(response);
      if (redirectErrorCode) {
        return new XhrStatusError(null, redirectErrorCode, response.status);
      }

      const isJsonResponse = response.headers.get("content-type")?.includes("json") ?? false;
      if (response.redirected && !isJsonResponse) {
        await navigateTo(response.url);
        return new XhrStatusOk<T>(response.url as T);
      }
      if (url.startsWith("/api")) {
        if (isJsonResponse && method !== "HEAD") {
          const data = (await response.json()) as T & { responseStatus?: { errorCode: string } };
          if (response.status === 200) {
            return new XhrStatusOk<T>(data);
          }

          return data.responseStatus
            ? new XhrStatusError(data.responseStatus, data.responseStatus.errorCode, response.status)
            : new XhrStatusError(data, response.statusText, response.status);
        }

        if (response.status === 200) {
          return new XhrStatusOk<T>((await response.text()) as T);
        }

        return new XhrStatusError(await response.text(), response.statusText, response.status);
      }

      if (!response.ok) {
        return new XhrStatusError(null, response.statusText, response.status);
      }

      if (!isJsonResponse) {
        return new XhrStatusOk<T>((await response.text()) as T);
      }

      const data = (await response.json()) as T & { payload?: T } & {
        error: boolean;
        message?: string;
        payload: string;
        status: number;
      };
      return !data.error
        ? new XhrStatusOk<T>(data.payload ?? data)
        : new XhrStatusError(data.payload ?? null, data.message ?? data.payload, data.status);
    } catch (errorThrown: unknown) {
      const redirectErrorCode = ensureSecureZoneRedirectFromError(errorThrown);
      if (redirectErrorCode) {
        const statusCode = redirectErrorCode === AUTH_REQUIRED_ERROR_CODE ? 401 : 403;
        return new XhrStatusError(null, redirectErrorCode, statusCode);
      }
      return new XhrStatusError(null, errorThrown as string, 500);
    }
  }

  async sendRawRequest(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
      const response = await fetch(input, this.buildRawRequestInit(input, init));
      await ensureSecureZoneRedirectFromResponse(response);
      return response;
    } catch (error) {
      ensureSecureZoneRedirectFromError(error);
      throw error;
    }
  }

  downloadRequest(url: string, values: Record<string, string>): Promise<boolean> {
    const formData = new FormData();
    formData.append("__RequestVerificationToken", this.antiForgeryToken);

    for (const key of Object.keys(values)) {
      formData.append(key, values[key]);
    }

    const triggerClick = (anchorElement: HTMLAnchorElement) => {
      let event: MouseEvent;

      try {
        event = new MouseEvent("click");
      } catch {
        event = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 0,
          screenX: 0,
          screenY: 0,
          clientX: 0,
          clientY: 0,
          ctrlKey: false,
          shiftKey: false,
          altKey: false,
          metaKey: false,
          button: 0,
          relatedTarget: null
        });
      }

      anchorElement.dispatchEvent(event);
    };

    return this.sendRawRequest(url, {
      method: "POST",
      body: formData
    }).then(async response => {
      if (!response.ok) {
        return false;
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "download";

      if (contentDisposition) {
        const parsedName = getContentDispositionFilename(contentDisposition);
        if (parsedName) {
          filename = parsedName;
        }
      }

      const blob = await response.blob();
      const file = new File([blob], filename, {
        type: "octet/stream"
      });

      const downloadUrl = window.URL.createObjectURL(file);

      const htmlAnchorElement = document.createElement("a");
      htmlAnchorElement.style.display = "none";
      htmlAnchorElement.setAttribute("href", downloadUrl);
      htmlAnchorElement.setAttribute("download", filename);
      document.body.appendChild(htmlAnchorElement);
      setTimeout(() => triggerClick(htmlAnchorElement), 0);

      return true;
    });
  }

  private isSameOriginRequest(input: RequestInfo | URL) {
    const url =
      typeof input === "string"
        ? input
        : typeof Request !== "undefined" && input instanceof Request
          ? input.url
          : input instanceof URL
            ? input.href
            : "";

    if (!url) {
      return false;
    }

    if (url.startsWith("/")) {
      return true;
    }

    if (typeof window === "undefined") {
      return false;
    }

    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private buildRawRequestInit(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
    if (!this.isSameOriginRequest(input)) {
      return init;
    }

    const headers = new Headers(typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      for (const [key, value] of new Headers(init.headers)) {
        headers.set(key, value);
      }
    }

    this.setCurrentLanguageHeader(headers);
    return {
      ...init,
      headers
    };
  }

  private setCurrentLanguageHeader(headers: Headers) {
    if (headers.has("X-Language-Tag")) {
      return;
    }
    const languageHeader = this.getCurrentLanguageHeader();
    if (languageHeader) {
      headers.set("X-Language-Tag", languageHeader);
    }
  }

  private getCurrentLanguageHeader() {
    if (typeof document === "undefined") {
      return "";
    }
    const language = document.documentElement.lang.trim().toLowerCase();
    return language.length > 0 ? language : "";
  }
}

export default new HttpClient();
